import type { SuburbInput } from "@repo/adapter-core";
import type { SoldOnlyAdapterConfig } from "../shared/sold-only.ts";

/**
 * eplace.com.au's frontend is a Vue.js SPA — the sold-listings page
 * loads with an empty grid and the listings hydrate client-side via XHR
 * to a public Azure Cognitive Search index. Capturing the rendered HTML
 * with Brightdata Web Unlocker returns only the SSR shell. Calling the
 * Azure Search endpoint directly is faster, cheaper, and gives us
 * structured JSON.
 *
 * The api-key is the public client-side key embedded in their JS bundle
 * (anyone can extract it). Azure Search itself doesn't bot-wall the
 * endpoint — see docs/scraper-routing.md "Place Estate Agents — Azure
 * Cognitive Search" for the discovery context.
 */
export const PLACE_AZURE_SEARCH_ENDPOINT =
  "https://eplacelive-v2.search.windows.net/indexes/eplacelive-index/docs/search?api-version=2016-09-01";

const PLACE_AZURE_API_KEY = "H2J5ISxeQq8TBqD5bHX3LdwHMlExIYa9T8a0R8zYWRAzSeCxJ4fd";

const SOLD_FILTER =
  "system_listing_state ne 'current' and system_listing_state ne 'withdrawn' and state_date ne null and state_date ne '' and feed_status ne 'stopped' and listing_sale_or_rental eq 'Sale' and tags/all(t: t ne 'LaunchingSoon') and tags/all(t: t ne 'eplace-onhold') and tags/all(t: t ne 'Coming2023') and tags/all(t: t ne 'coming2024') and is_deleted eq false";

const PAGE_SIZE = 50;

export function buildSearchUrl(_suburb: SuburbInput, _config: SoldOnlyAdapterConfig): string {
  // Kept for AdapterDefinition contract compatibility. The discover worker
  // uses `directApiDiscovery` for this adapter — buildSearchUrl is a
  // human-readable pointer at the API endpoint.
  return PLACE_AZURE_SEARCH_ENDPOINT;
}

export function buildPlaceSearchBody(
  suburb: SuburbInput,
  _config: SoldOnlyAdapterConfig,
  options: { skip?: number; top?: number } = {},
): Record<string, unknown> {
  return {
    filter: SOLD_FILTER,
    orderby: "state_date desc",
    search: `${suburb.locality} ${suburb.state.toUpperCase()} ${suburb.postcode}`,
    searchFields: "address_formats_full_address_w_building_name",
    scoringProfile: "addressBoost",
    top: options.top ?? PAGE_SIZE,
    skip: options.skip ?? 0,
    count: true,
  };
}

interface PlaceSearchHit {
  default_external_id?: string;
  address_postcode?: string;
  address_suburb_or_town?: string;
  system_listing_state?: string;
}

interface PlaceSearchResponse {
  "@odata.count"?: number;
  value?: PlaceSearchHit[];
}

export function parsePlaceSearchResponse(
  json: unknown,
  baseUrl: string,
): { propertyUrls: string[]; totalCount?: number; hasNextPage: boolean } {
  const response = json as PlaceSearchResponse;
  const hits = Array.isArray(response?.value) ? response.value : [];

  const propertyUrls = hits
    .filter((hit) => hit.system_listing_state === "sold" && typeof hit.default_external_id === "string")
    .map((hit) => `${baseUrl}/properties?external_id=${hit.default_external_id}`);

  const totalCount = typeof response?.["@odata.count"] === "number" ? response["@odata.count"] : undefined;
  const hasNextPage = totalCount !== undefined && hits.length === PAGE_SIZE && hits.length < totalCount;

  return { propertyUrls, totalCount, hasNextPage };
}

export const PLACE_AZURE_HEADERS: Record<string, string> = {
  "Content-Type": "application/json",
  "api-key": PLACE_AZURE_API_KEY,
};
